import React, { useState, forwardRef } from 'react';
import { Button, Table, Spin, Tooltip } from 'antd';
import axios from 'axios';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../components/common/CustomModal';

export const Axios = axios.create({
  baseURL: process.env.REACT_APP_APPI_ADDRESS,
});

// Modal para mostrar detalles completos de una factura
function InvoiceDetailsModal({ isOpen, onCancel, invoice }) {
  const { t } = useTranslation();
  const productColumns = [
    { title: t('invoice.col_nombre_producto'), dataIndex: 'namegood', key: 'namegood' },
    { title: t('invoice.col_cantidad'), dataIndex: 'quantity', key: 'quantity' },
    { title: t('invoice.col_peso'), dataIndex: 'weight', key: 'weight' },
    { title: t('invoice.col_dimensiones'), dataIndex: 'dimensions', key: 'dimensions' },
  ];

  return (
    <CustomModal
      open={isOpen}
      onCancel={onCancel}
      title={t('invoice.invoice_details')}
      subtitle={`Código: ${invoice?.codigo || ''}`}
      icon={<EyeOutlined />}
      width="70%"
    >
      {invoice && (
        <div>
            {/* Sección de Información General */}
            <div style={{
              background: 'linear-gradient(135deg, #f8f9fc 0%, #e8ebf7 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid #e0e6ff'
            }}>
              <h3 style={{ color: '#253289', marginBottom: '16px', fontSize: '16px' }}>
                📄 Información General
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
                <div><strong>{t('invoice.col_fecha_reserva')}:</strong> <span style={{ color: '#666' }}>{invoice.rdatereserve}</span></div>
                <div><strong>{t('invoice.col_stateinvoice')}:</strong> <span style={{ color: '#666' }}>{invoice.estado}</span></div>
                <div><strong>{t('invoice.col_priority')}:</strong> <span style={{ color: '#666' }}>{invoice.prioridad}</span></div>
              </div>
            </div>

            {/* Sección de Destinatario */}
            <div style={{
              background: 'linear-gradient(135deg, #f8f9fc 0%, #e8ebf7 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid #e0e6ff'
            }}>
              <h3 style={{ color: '#253289', marginBottom: '16px', fontSize: '16px' }}>
                👤 Destinatario
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
                <div><strong>{t('invoice.col_destinatario')}:</strong> <span style={{ color: '#666' }}>{invoice.destinatario}</span></div>
                <div><strong>{t('invoice.col_consigneephone')}:</strong> <span style={{ color: '#666' }}>{invoice.consigneephone}</span></div>
                <div><strong>{t('invoice.col_identidad')}:</strong> <span style={{ color: '#666' }}>{invoice.cidentity}</span></div>
                <div style={{ gridColumn: '1 / -1' }}><strong>{t('invoice.col_direccion')}:</strong> <span style={{ color: '#666' }}>{invoice.direccion}</span></div>
              </div>
            </div>

            {/* Sección de Remitente */}
            <div style={{
              background: 'linear-gradient(135deg, #f8f9fc 0%, #e8ebf7 100%)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid #e0e6ff'
            }}>
              <h3 style={{ color: '#253289', marginBottom: '16px', fontSize: '16px' }}>
                📤 Remitente
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
                <div><strong>{t('invoice.col_remitente')}:</strong> <span style={{ color: '#666' }}>{invoice.sname}</span></div>
                <div><strong>{t('invoice.col_shipperphone')}:</strong> <span style={{ color: '#666' }}>{invoice.shipperphone}</span></div>
              </div>
            </div>

            {/* Sección de Productos */}
            <div style={{
              background: 'linear-gradient(135deg, #f8f9fc 0%, #e8ebf7 100%)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e0e6ff'
            }}>
              <h3 style={{ color: '#253289', marginBottom: '16px', fontSize: '16px' }}>
                📦 Productos
              </h3>
              <Table
                columns={productColumns}
                dataSource={invoice.productos}
                pagination={false}
                rowKey="idproductinvoice"
                size="small"
                style={{
                  background: 'white',
                  borderRadius: '8px'
                }}
              />
            </div>
        </div>
      )}
    </CustomModal>
  );
}

// Componente principal para ver detalles de ruta e invocar modal de factura
const VerDetallesRutaButton = forwardRef(({ idruta, headers }, ref) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedInv, setSelectedInv] = useState(null);
  const [invModalOpen, setInvModalOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setLoading(true);
    Axios.post('sd/getDetallesRuta', { idruta }, { headers })
      .then(response => {
        const raw = Array.isArray(response.data.data) ? response.data.data : [];
        const mapped = raw.map(f => {
          let productos = f.productos;
          if (typeof productos === 'string') {
            try { productos = JSON.parse(productos); } catch { productos = []; }
          }
          if (!Array.isArray(productos)) productos = productos ? [productos] : [];
          return {
            key: f.codigo,
            codigo: f.codigo,
            rdatereserve: f.rdatereserve,
            estado: f.estado,
            prioridad: f.prioridad,
            destinatario: f.destinatario,
            telefono_destinatario: f.telefono_destinatario,
            direccion: f.direccion_destinatario,
            municipio: f.municipio,
            provincia: f.provincia,
            consigneephone: f.consigneephone,
            cidentity: f.cidentity,
            sname: f.sname,
            shipperphone: f.shipperphone,
            productos,
          };
        });
        setFacturas(mapped);
      })
      .catch(err => {
        console.error('Error al cargar detalles de ruta:', err);
        setFacturas([]);
      })
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setOpen(false);
    setFacturas([]);
  };

  const invoiceColumns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      render: (text, record) => (
        <button
          onClick={() => { setSelectedInv(record); setInvModalOpen(true); }}
          style={{
            background: 'none',
            border: 'none',
            color: '#1890ff',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {text}
        </button>
      )
    },
    { title: 'Destinatario', dataIndex: 'destinatario', key: 'destinatario' },
    { title: 'Dirección', dataIndex: 'direccion', key: 'direccion' },
    { title: 'Municipio', dataIndex: 'municipio', key: 'municipio' },
    { title: 'Provincia', dataIndex: 'provincia', key: 'provincia' },
    { title: 'Estado', dataIndex: 'estado', key: 'estado' },
  ];

  return (
    <>
      <Tooltip title={t('ruta.more_details')}>
        <Button
          ref={ref}
          type="primary"
          icon={<EyeOutlined />}
          onClick={handleOpen}
          disabled={!idruta}
          size="small"
          style={{
            background: idruta
              ? 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)'
              : '#d9d9d9',
            borderColor: idruta
              ? '#253289'
              : '#d9d9d9',
            borderRadius: '8px',
            height: '32px',
            fontWeight: '500',
            color: idruta
              ? 'white'
              : '#000000',
            boxShadow: idruta
              ? '0 2px 8px rgba(37, 50, 137, 0.3)'
              : 'none'
          }}
        >
          {t('ruta.more_details')}
        </Button>
      </Tooltip>

      <CustomModal
        open={open}
        onCancel={handleClose}
        title={t('ruta.more_details')}
        subtitle="Facturas asociadas a la ruta"
        icon={<EyeOutlined />}
        width={1000}
      >
        <div>
          {/* Contenido del modal */}
          {loading ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <Spin size="large" />
                <p style={{ color: '#666', margin: 0 }}>Cargando detalles de la ruta...</p>
              </div>
            ) : (
              <div style={{
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e0e6ff',
                overflow: 'hidden'
              }}>
                <Table
                  dataSource={facturas}
                  columns={invoiceColumns}
                  rowKey="codigo"
                  pagination={false}
                  scroll={{ y: 400 }}
                  style={{
                    background: 'white',
                  }}
                />
              </div>
            )}
        </div>
      </CustomModal>

      <InvoiceDetailsModal
        isOpen={invModalOpen}
        onCancel={() => setInvModalOpen(false)}
        invoice={selectedInv}
      />
    </>
  );
});

export default VerDetallesRutaButton;
